import { createContext, useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { PATH_AUTH } from 'src/routes/paths';
import { userApi } from '../api/user';
import { Roles } from '../types/User';
import { UserContextType } from './types';
import { useAuthContext } from './useAuthContext';

// ----------------------------------------------------------------------

export const UserContext = createContext<UserContextType | null>(null);

type UserProviderProps = {
  children: React.ReactNode;
};

export function UserProvider({ children }: UserProviderProps) {
  const { logout } = useAuthContext();
  const { isAuthenticated } = useAuthContext();
  const { refetch, data, isFetching, isError, error } = useQuery(
    ['user'],
    () => userApi.userControllerGetCurrentUser(),
    {
      enabled: false,
      retry: (failureCount, err: any) => {
        // Don't retry if user is inactive
        if (err?.response?.status === 403 && err?.response?.data?.message === 'USER_INACTIVE') {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: 2000,
    },
  );

  useEffect(() => {
    if (isAuthenticated && isError) {
      const axiosError = error as any;
      // Check if error is USER_INACTIVE (403 Forbidden)
      if (axiosError?.response?.status === 403 && axiosError?.response?.data?.message === 'USER_INACTIVE') {
        logout();
        window.location.replace(PATH_AUTH.accountClosed);
        return;
      }
      logout();
      window.location.replace(PATH_AUTH.login);
    }
  }, [isError, isAuthenticated, logout, error]);

  useEffect(() => {
    if (isAuthenticated) {
      refetch();
    }
  }, [isAuthenticated, refetch]);

  const memoizedValue = useMemo(() => {
    const hasUserRole = data?.data?.roles?.includes(Roles.user);
    const hasProfessionalRole = data?.data?.roles?.includes(Roles.professional);
    const hasAdminRole = data?.data?.roles?.includes(Roles.admin);

    const hasOrganizationAdminRole =
      data?.data?.roles?.includes(Roles.organization_admin) ||
      data?.data?.organizations?.some((org: any) =>
        org.roles?.includes(Roles.organization_admin),
      );
    const hasOrganizationOwnerRole =
      data?.data?.roles?.includes(Roles.organization_owner) ||
      data?.data?.organizations?.some((org: any) =>
        org.roles?.includes(Roles.organization_owner),
      );

    return {
      data: data?.data,
      isOnboarded: data?.data?.onboarded,
      isLoading: isFetching,
      isUserRole:
        hasUserRole &&
        !hasProfessionalRole &&
        !hasAdminRole &&
        !hasOrganizationAdminRole &&
        !hasOrganizationOwnerRole,
      isProfessionalRole: hasProfessionalRole,
      isAdminRole: hasAdminRole,
      isOrganizationAdminRole: hasOrganizationAdminRole,
      isOrganizationOwnerRole: hasOrganizationOwnerRole,
      isError,
      refetch,
    };
  }, [data, isFetching, isError, refetch]);

  return (
    <UserContext.Provider value={memoizedValue}>
      {children}
    </UserContext.Provider>
  );
}
